// Simplex noise implementation for terrain generation
// Based on Stefan Gustavson's implementation
const grad3 = [
    { x: 1, y: 1, z: 0 },
    { x: -1, y: 1, z: 0 },
    { x: 1, y: -1, z: 0 },
    { x: -1, y: -1, z: 0 },
    { x: 1, y: 0, z: 1 },
    { x: -1, y: 0, z: 1 },
    { x: 1, y: 0, z: -1 },
    { x: -1, y: 0, z: -1 },
    { x: 0, y: 1, z: 1 },
    { x: 0, y: -1, z: 1 },
    { x: 0, y: 1, z: -1 },
    { x: 0, y: -1, z: -1 },
];
const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;
export const createNoiseGenerator = ({ seed }) => {
    // IMPORTANT: These must be instance-local, not module-level globals!
    // Each noise generator needs its own permutation table.
    const perm = new Array(512);
    const gradP = new Array(512);
    // Initialize permutation table with seed
    const p = [];
    // Simple seeded random - use local seed copy to avoid mutation issues
    let localSeed = seed;
    const seededRandom = () => {
        const x = Math.sin(localSeed++) * 10000;
        return x - Math.floor(x);
    };
    // Create base permutation
    for (let i = 0; i < 256; i++) {
        p[i] = i;
    }
    // Shuffle using seeded random
    for (let i = 255; i > 0; i--) {
        const j = Math.floor(seededRandom() * (i + 1));
        const temp = p[i];
        p[i] = p[j] ?? 0;
        p[j] = temp ?? 0;
    }
    // Extend permutation table
    for (let i = 0; i < 512; i++) {
        perm[i] = p[i & 255] ?? 0;
        gradP[i] = grad3[(perm[i] ?? 0) % 12] ?? { x: 0, y: 0, z: 0 };
    }
    // Calculate corner contribution for simplex noise
    const cornerContribution = ({ dx, dy, grad, }) => {
        let t = 0.5 - dx * dx - dy * dy;
        if (t < 0) {
            return 0;
        }
        t *= t;
        return t * t * (grad.x * dx + grad.y * dy);
    };
    const noise2D = ({ x, y }) => {
        // Skew the input space to determine which simplex cell we're in
        const s = (x + y) * F2;
        const i = Math.floor(x + s);
        const j = Math.floor(y + s);
        const t = (i + j) * G2;
        const X0 = i - t;
        const Y0 = j - t;
        const x0 = x - X0;
        const y0 = y - Y0;
        // Determine which simplex we're in
        const i1 = x0 > y0 ? 1 : 0;
        const j1 = x0 > y0 ? 0 : 1;
        const x1 = x0 - i1 + G2;
        const y1 = y0 - j1 + G2;
        const x2 = x0 - 1 + 2 * G2;
        const y2 = y0 - 1 + 2 * G2;
        // Calculate gradient indices
        const ii = i & 255;
        const jj = j & 255;
        const gi0 = gradP[ii + (perm[jj] ?? 0)] ?? { x: 0, y: 0, z: 0 };
        const gi1 = gradP[ii + i1 + (perm[jj + j1] ?? 0)] ?? { x: 0, y: 0, z: 0 };
        const gi2 = gradP[ii + 1 + (perm[jj + 1] ?? 0)] ?? { x: 0, y: 0, z: 0 };
        // Calculate contribution from the three corners
        const n0 = cornerContribution({ dx: x0, dy: y0, grad: gi0 });
        const n1 = cornerContribution({ dx: x1, dy: y1, grad: gi1 });
        const n2 = cornerContribution({ dx: x2, dy: y2, grad: gi2 });
        // Scale to [-1, 1]
        return 70 * (n0 + n1 + n2);
    };
    const octave2D = ({ x, y, octaves, persistence, lacunarity, }) => {
        let total = 0;
        let frequency = 1;
        let amplitude = 1;
        let maxValue = 0;
        for (let i = 0; i < octaves; i++) {
            total += noise2D({ x: x * frequency, y: y * frequency }) * amplitude;
            maxValue += amplitude;
            amplitude *= persistence;
            frequency *= lacunarity;
        }
        return total / maxValue;
    };
    return { noise2D, octave2D };
};
//# sourceMappingURL=noise.js.map